/**
 * Theme resolution.
 *
 * DESIGN.md section 5: selection is `[data-theme="ink-clinical-dark"]`,
 * defaulting from `@media (prefers-color-scheme: dark)`, with a user override
 * persisted per account.
 *
 * **The theme is never inferred from the time of day.** A night-shift worker in
 * a bright office and a day worker in a dim room both exist. There is a test in
 * `theme.spec.ts` that reads this file and fails if a clock API appears in it,
 * because the rule is easier to state than to remember.
 *
 * The three attribute states below are the exact contract of the generated
 * stylesheet, which is why `'system'` *removes* the attribute rather than
 * setting it: `packages/ui/generated/tokens.css` gates the media-query block on
 * `:root:not([data-theme])` — on the attribute's absence, not on the light
 * theme's name. That is what let the 2026-08-14 rename change these strings
 * without touching the generator.
 */

export const THEMES = ['ink-clinical', 'ink-clinical-dark'] as const
export type ThemeName = (typeof THEMES)[number]

/** `'system'` follows the OS. The other two are explicit per-account overrides. */
export const THEME_PREFERENCES = ['system', ...THEMES] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]

export const THEME_LABELS: Record<ThemePreference, string> = {
  system: 'Match my system',
  'ink-clinical': 'Light',
  'ink-clinical-dark': 'Dark'
}

const DARK_QUERY = '(prefers-color-scheme: dark)'

export function isThemePreference (value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value)
}

/** What the operating system is asking for, right now. */
export function systemTheme (): ThemeName {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'ink-clinical'
  return window.matchMedia(DARK_QUERY).matches ? 'ink-clinical-dark' : 'ink-clinical'
}

/** The theme that will actually render, given a preference. */
export function resolveTheme (preference: ThemePreference): ThemeName {
  return preference === 'system' ? systemTheme() : preference
}

/**
 * Writes the preference to the document root.
 *
 * `color-scheme` is set alongside `data-theme` so native form controls,
 * scrollbars and the browser's own canvas follow the app rather than the OS.
 * Without it a dark app renders light scrollbars.
 */
export function applyTheme (preference: ThemePreference, root: HTMLElement): void {
  if (preference === 'system') {
    root.removeAttribute('data-theme')
    root.style.colorScheme = 'light dark'
    return
  }
  root.setAttribute('data-theme', preference)
  root.style.colorScheme = preference === 'ink-clinical-dark' ? 'dark' : 'light'
}

/**
 * Calls back whenever the OS preference changes. Only meaningful while the
 * preference is `'system'`; callers are expected to check.
 *
 * Returns the unsubscribe function.
 */
export function watchSystemTheme (onChange: (theme: ThemeName) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(DARK_QUERY)
  const handler = (event: MediaQueryListEvent): void => {
    onChange(event.matches ? 'ink-clinical-dark' : 'ink-clinical')
  }
  query.addEventListener('change', handler)
  return () => query.removeEventListener('change', handler)
}
